import React, { useEffect, useState, useCallback, useRef } from 'react';

function getGraticuleInterval(zoom) {
  if (zoom >= 14) return 0.005;
  if (zoom >= 12) return 0.01;
  if (zoom >= 10) return 0.05;
  if (zoom >= 8) return 0.1;
  if (zoom >= 6) return 0.5;
  if (zoom >= 4) return 1;
  if (zoom >= 2) return 5;
  return 10;
}

function decimalsForInterval(interval) {
  return Math.max(0, Math.ceil(-Math.log10(interval)));
}

function formatLng(v, decimals) {
  const abs = Math.abs(v).toFixed(decimals);
  return `${abs}\u00b0${v >= 0 ? 'E' : 'W'}`;
}

function formatLat(v, decimals) {
  const abs = Math.abs(v).toFixed(decimals);
  return `${abs}\u00b0${v >= 0 ? 'N' : 'S'}`;
}

function computeLabels(map) {
  const bounds = map.getBounds();
  const zoom = map.getZoom();
  const interval = getGraticuleInterval(zoom);
  const container = map.getContainer();
  const W = container.offsetWidth;
  const H = container.offsetHeight;
  const decimals = decimalsForInterval(interval);

  const west = bounds.getWest();
  const east = bounds.getEast();
  const north = bounds.getNorth();
  const south = bounds.getSouth();

  // Longitude labels along the bottom edge
  const lngLabels = [];
  const startLng = Math.ceil(west / interval) * interval;
  for (let lng = startLng; lng <= east + 1e-9; lng += interval) {
    const p = map.project([lng, south]);
    if (p.x >= 0 && p.x <= W) {
      lngLabels.push({ x: p.x, label: formatLng(lng, decimals) });
    }
  }

  // Latitude labels along the left edge
  const latLabels = [];
  const startLat = Math.ceil(south / interval) * interval;
  for (let lat = startLat; lat <= north + 1e-9; lat += interval) {
    const p = map.project([west, lat]);
    if (p.y >= 0 && p.y <= H) {
      latLabels.push({ y: p.y, label: formatLat(lat, decimals) });
    }
  }

  return { lngLabels, latLabels, W, H };
}

const GRATICULE_LAYER_ID = 'graticule-lines';

function GraticuleOverlay({ map }) {
  const [labels, setLabels] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const rafRef = useRef(null);

  const update = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!map) return;
      setLabels(computeLabels(map));
    });
  }, [map]);

  // Map move → update edge labels
  useEffect(() => {
    if (!map) return;
    update();
    map.on('move', update);
    map.on('resize', update);
    return () => {
      map.off('move', update);
      map.off('resize', update);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [map, update]);

  // Graticule layer hover → show tooltip
  useEffect(() => {
    if (!map) return;

    const onMove = e => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: [GRATICULE_LAYER_ID]
      });
      if (!features.length) {
        setTooltip(null);
        return;
      }
      const f = features[0];
      const kind = f.properties && f.properties.kind;
      const value = f.properties && f.properties.value;
      if (kind == null || value == null) return;
      const dec = decimalsForInterval(getGraticuleInterval(map.getZoom()));
      const label =
        kind === 'meridian' ? formatLng(value, dec) : formatLat(value, dec);
      setTooltip({ x: e.point.x, y: e.point.y, label });
    };

    const onLeave = () => setTooltip(null);

    map.on('mousemove', GRATICULE_LAYER_ID, onMove);
    map.on('mouseleave', GRATICULE_LAYER_ID, onLeave);
    return () => {
      map.off('mousemove', GRATICULE_LAYER_ID, onMove);
      map.off('mouseleave', GRATICULE_LAYER_ID, onLeave);
    };
  }, [map]);

  if (!labels) return null;
  const { lngLabels, latLabels, H } = labels;

  return (
    <svg
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 2,
        overflow: 'visible'
      }}
    >
      {/* Bottom edge: longitude labels */}
      {lngLabels.map(({ x, label }, i) => (
        <text
          key={i}
          x={x}
          y={H - 6}
          textAnchor="middle"
          fontSize={10}
          fontFamily="Arial,sans-serif"
          fill="#111"
          stroke="rgba(255,255,255,0.8)"
          strokeWidth={2.5}
          paintOrder="stroke"
        >
          {label}
        </text>
      ))}

      {/* Left edge: latitude labels */}
      {latLabels.map(({ y, label }, i) => (
        <text
          key={i}
          x={4}
          y={y - 3}
          fontSize={10}
          fontFamily="Arial,sans-serif"
          fill="#111"
          stroke="rgba(255,255,255,0.8)"
          strokeWidth={2.5}
          paintOrder="stroke"
        >
          {label}
        </text>
      ))}

      {/* Hover tooltip */}
      {tooltip && (
        <g>
          <rect
            x={tooltip.x + 8}
            y={tooltip.y - 18}
            width={tooltip.label.length * 6.5 + 8}
            height={18}
            rx={3}
            fill="rgba(255,255,255,0.92)"
            stroke="#aaa"
            strokeWidth={0.8}
          />
          <text
            x={tooltip.x + 12}
            y={tooltip.y - 5}
            fontSize={11}
            fontFamily="Arial,sans-serif"
            fill="#111"
          >
            {tooltip.label}
          </text>
        </g>
      )}
    </svg>
  );
}

export default GraticuleOverlay;
